/**
 * Plate Carrier Konfigurator V2
 * Multi-carrier Alpine.js + Konva.js configurator
 *
 * Rendering: Layer-Stacking nach konf-manifest.json (Master-Koordinaten,
 * generiert von tools/konfigurator/compose.py). Teile ohne Manifest-Eintrag
 * fallen auf contain-Fit im Zonen-Rechteck zurück.
 */

const colorAliases = [
  ['multicam', 'multicam original', 'camouflage', 'camo', 'mc'],
  ['olive', 'olive green', 'olive drab', 'od', 'od green'],
  ['coyote', 'coyote tan', 'coyote brown', 'tan', 'fde'],
  ['schwarz', 'black', 'noir'],
  ['ranger green', 'adaptive green'],
  ['grau', 'grey', 'gray', 'wolf grey', 'wolf gray'],
];

function colorMatch(a, b) {
  if (!a || !b) return false;
  const na = a.trim().toLowerCase();
  const nb = b.trim().toLowerCase();
  if (na === nb) return true;
  for (const group of colorAliases) {
    if (group.includes(na) && group.includes(nb)) return true;
  }
  return false;
}

function findByColor(map, color) {
  if (!map) return null;
  for (const [key, val] of Object.entries(map)) {
    if (colorMatch(key, color)) return val;
  }
  return null;
}

const fallbackColorMap = {
  'multicam': '#6B5B3A',
  'multicam black': '#2A2A2A',
  'multicam tropic': '#3D5A3A',
  'ranger green': '#4A5D23',
  'adaptive green': '#4A5D23',
  'olive': '#556B2F',
  'olive green': '#556B2F',
  'od green': '#3C5A14',
  'coyote': '#8B7355',
  'coyote tan': '#8B7355',
  'coyote brown': '#8B7355',
  'tan': '#D2B48C',
  'schwarz': '#1a1a1a',
  'black': '#1a1a1a',
  'shadow grey': '#5A5A5A',
  'wolf grey': '#7A7A7A',
  'grey': '#808080',
  'grau': '#808080',
  'woodland': '#4A5A2A',
  'flecktarn': '#5A6B3C',
};

function resolveHex(nameOrHex) {
  if (!nameOrHex) return '#888';
  const v = nameOrHex.trim();
  return v.startsWith('#') ? v : (fallbackColorMap[v.toLowerCase()] || '#888');
}

function formatMoney(cents, format) {
  const amount = (cents / 100).toFixed(2);
  const withComma = amount.replace('.', ',');
  return (format || '{{amount_with_comma_separator}} €')
    .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, withComma)
    .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, Math.round(cents / 100).toString())
    .replace(/\{\{\s*amount\s*\}\}/g, amount);
}

function registerConfigurator() {
  if (typeof Alpine === 'undefined') return;

  Alpine.data('configurator', () => {
    const data = window.__configuratorData || {};

    return {
      carriers: data.carriers || [],
      attachments: data.attachments || [],
      moneyFormat: data.moneyFormat || '{{amount_with_comma_separator}} €',
      cartAddUrl: data.cartAddUrl || '/cart/add.js',
      addToCartText: data.addToCartText || 'Zum Warenkorb hinzufügen',
      canvasWidth: data.canvasWidth || 800,
      canvasHeight: data.canvasHeight || 1000,

      selectedCarrier: '',
      selectedColor: '',
      selectedSize: '',
      currentView: 'front',
      selectedAttachments: [],
      attachmentColors: {},
      activeZone: '',
      isAdding: false,
      addedSuccess: false,
      cartError: '',

      _manifest: null,
      _assetBase: '',
      _stage: null,
      _layer: null,
      _nodeMap: {},
      _imageCache: {},
      _drawSeq: 0,

      colorMatch,

      async init() {
        if (data.manifestUrl) {
          this._assetBase = data.manifestUrl.replace(/konf-manifest\.json.*$/, '');
          // Cache-Busting: Version des Manifests an alle Layer-URLs anhängen,
          // sonst liefern CDN/Browser alte PNGs unter der nackten URL aus
          this._assetVersion = (data.manifestUrl.match(/\?(v=\d+)/) || [])[1] || '';
          try {
            const res = await fetch(data.manifestUrl);
            if (res.ok) this._manifest = await res.json();
          } catch { /* Manifest optional — Fallback auf Zonen-Fit */ }
        }
        if (this._manifest) {
          this.canvasWidth = this._manifest.masterW;
          this.canvasHeight = this._manifest.masterH;
          this._applyManifestZones();
        }
        if (this.carriers.length) this.selectCarrier(this.carriers[0].handle);
        this.$watch('activeZone', () => this.redrawCanvas());
        this.$nextTick(() => this.initCanvas());

        let resizeTimer;
        window.addEventListener('resize', () => {
          clearTimeout(resizeTimer);
          resizeTimer = setTimeout(() => this.fitStageToContainer(), 100);
        });
      },

      // Manifest-Zone gewinnt über Metafield-Zone, damit Hotspots,
      // Tile-Filter und Rendering dieselbe Geometrie nutzen.
      // Carrier ohne Manifest-Eintrag werden ausgeblendet — deren Metafield-
      // Basisbilder (Model-Fotos) können nicht sauber gerendert werden.
      _applyManifestZones() {
        this.carriers = this.carriers.filter(c => this._manifest.carriers[c.handle]);
        for (const att of this.attachments) {
          const entry = this._manifestEntry(att.id);
          if (entry) att.zone = entry.zone;
        }
        for (const carrier of this.carriers) {
          const mc = this._manifest.carriers[carrier.handle];
          if (!mc) continue;
          carrier.zones = Object.entries(mc.zones).map(([id, z]) => ({
            id,
            label: id.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
            view: z.view || 'front',
            x: z.x * 100,
            y: z.y * 100,
            width: z.w * 100,
            height: z.h * 100,
          }));
        }
      },

      _manifestCarrier() {
        return (this._manifest && this._manifest.carriers[this.selectedCarrier]) || null;
      },

      _manifestEntry(attId) {
        const current = this._manifestCarrier();
        if (current && current.parts[attId]) return current.parts[attId];
        for (const mc of Object.values((this._manifest || {}).carriers || {})) {
          if (mc.parts[attId]) return mc.parts[attId];
        }
        return null;
      },

      _assetUrl(file) {
        return this._assetBase + file + (this._assetVersion ? '?' + this._assetVersion : '');
      },

      // --- Computed ---

      get currentCarrier() {
        return this.carriers.find(c => c.handle === this.selectedCarrier) || null;
      },

      get allZones() {
        return this.currentCarrier ? this.currentCarrier.zones : [];
      },

      get currentZones() {
        return this.allZones.filter(z => z.view === this.currentView);
      },

      get hasBackView() {
        const mc = this._manifestCarrier();
        if (mc) return !!mc.baseBack;
        return this.currentCarrier
          ? Object.keys(this.currentCarrier.backImages || {}).length > 0
          : false;
      },

      get currentColors() {
        if (!this.currentCarrier) return [];
        const mc = this._manifestCarrier();
        const seen = new Set();
        return this.currentCarrier.variants.reduce((acc, v) => {
          if (v.option1 && !seen.has(v.option1)) {
            seen.add(v.option1);
            // Nur Farben anbieten, für die eine Manifest-Basis existiert
            if (mc && !findByColor(mc.base, v.option1)) return acc;
            const cd = (this.currentCarrier.colors || []).find(c => c.name === v.option1);
            acc.push({ name: v.option1, hex: resolveHex(cd ? cd.hex : v.option1) });
          }
          return acc;
        }, []);
      },

      get currentSizes() {
        if (!this.currentCarrier) return [];
        const seen = new Set();
        return this.currentCarrier.variants.reduce((acc, v) => {
          if (v.option2 && !seen.has(v.option2)) {
            seen.add(v.option2);
            acc.push(v.option2);
          }
          return acc;
        }, []);
      },

      get filteredAttachments() {
        if (!this.currentCarrier) return [];
        return this.attachments.filter(att => {
          if (att.zone !== this.activeZone) return false;
          if (att.compatibleCarriers && att.compatibleCarriers.length) {
            return att.compatibleCarriers.includes(this.selectedCarrier);
          }
          return true;
        });
      },

      get selectedItems() {
        return this.selectedAttachments
          .map(id => this.attachments.find(a => a.id === id))
          .filter(Boolean);
      },

      _carrierVariant() {
        if (!this.currentCarrier) return null;
        const vs = this.currentCarrier.variants;
        return vs.find(v => colorMatch(v.option1, this.selectedColor) && v.option2 === this.selectedSize)
          || vs.find(v => colorMatch(v.option1, this.selectedColor))
          || null;
      },

      _attVariant(att) {
        const attColor = this.getAttColor(att.id);
        return (att.variants || []).find(v => colorMatch(v.option1, attColor) && v.available)
          || null;
      },

      // Varianten-echte Preise (Farbe/Größe), damit Zeilen und Summe übereinstimmen
      carrierPrice() {
        const cv = this._carrierVariant();
        return cv ? cv.price : (this.currentCarrier ? this.currentCarrier.price : 0);
      },

      attPrice(att) {
        const av = this._attVariant(att);
        return av ? av.price : att.price;
      },

      get totalPrice() {
        if (!this.currentCarrier) return 0;
        let sum = this.carrierPrice();
        for (const att of this.selectedItems) sum += this.attPrice(att);
        return sum;
      },

      // --- Actions ---

      selectCarrier(handle) {
        this.selectedCarrier = handle;
        this.selectedAttachments = [];
        this.attachmentColors = {};
        this.currentView = 'front';
        if (this.currentCarrier) {
          this.selectedColor = this.currentColors.length ? this.currentColors[0].name : '';
          this.selectedSize = this.currentSizes.length ? this.currentSizes[0] : '';
          this.activeZone = this.currentZones.length ? this.currentZones[0].id : '';
          this.$nextTick(() => this.redrawCanvas());
        }
      },

      setColor(name) {
        this.selectedColor = name;
        this.redrawCanvas();
      },

      setSize(size) {
        this.selectedSize = size;
      },

      toggleView() {
        this.currentView = this.currentView === 'front' ? 'back' : 'front';
        const visible = this.currentZones;
        if (!visible.find(z => z.id === this.activeZone)) {
          this.activeZone = visible.length ? visible[0].id : '';
        }
        this.redrawCanvas();
      },

      toggleAttachment(att) {
        const idx = this.selectedAttachments.indexOf(att.id);
        if (idx >= 0) {
          this.selectedAttachments.splice(idx, 1);
        } else {
          const zone = this.allZones.find(z => z.id === att.zone);
          if (zone && zone.only_one) {
            this.selectedAttachments = this.selectedAttachments.filter(id => {
              const existing = this.attachments.find(a => a.id === id);
              return !existing || existing.zone !== att.zone;
            });
          }
          this.selectedAttachments.push(att.id);
        }
        this.redrawCanvas();
      },

      removeAttachment(id) {
        const idx = this.selectedAttachments.indexOf(id);
        if (idx >= 0) this.selectedAttachments.splice(idx, 1);
        delete this.attachmentColors[id];
        this.redrawCanvas();
      },

      setAttColor(attId, color) {
        this.attachmentColors[attId] = color;
        this.redrawCanvas();
      },

      getAttColor(attId) {
        if (this.attachmentColors[attId]) return this.attachmentColors[attId];
        const att = this.attachments.find(a => a.id === attId);
        if (att && att.colors && att.colors.length) {
          const match = att.colors.find(c => colorMatch(c.name, this.selectedColor));
          return match ? match.name : att.colors[0].name;
        }
        return this.selectedColor;
      },

      getAttDisplayImage(att) {
        const color = this.getAttColor(att.id);
        if (att.colors && att.colors.length) {
          const cm = att.colors.find(c => colorMatch(c.name, color));
          if (cm && cm.image) return cm.image;
        }
        if (att.variants && att.variants.length) {
          const v = att.variants.find(va => colorMatch(va.option1, color) && va.image);
          if (v) return v.image;
        }
        return att.overlay || att.thumbnail || '';
      },

      getOverlayUrl(att) {
        const entry = this._manifestEntry(att.id);
        if (entry) {
          const file = findByColor(entry.images, this.getAttColor(att.id))
            || Object.values(entry.images)[0];
          if (file) return this._assetUrl(file);
        }
        // Freigestelltes Legacy-Overlay aus der Manifest-Map (existenzgeprüft)
        const legacy = (this._manifest?.overlays || {})[att.id.replace(/®/g, '')];
        if (legacy) return this._assetUrl(legacy);
        if (att.overlay) return att.overlay;
        const color = this.getAttColor(att.id);
        const cm = (att.colors || []).find(c => colorMatch(c.name, color));
        if (cm && cm.image) return cm.image;
        const vm = (att.variants || []).find(v => colorMatch(v.option1, color) && v.image);
        if (vm) return vm.image;
        return att.thumbnail || '';
      },

      isSelected(id) {
        return this.selectedAttachments.includes(id);
      },

      formatPrice(cents) {
        return formatMoney(cents, this.moneyFormat);
      },

      resolveHex(nameOrHex) {
        return resolveHex(nameOrHex);
      },

      // --- Canvas ---

      loadImage(url) {
        if (this._imageCache[url]) return Promise.resolve(this._imageCache[url]);
        return new Promise((resolve, reject) => {
          const img = new Image();
          img.crossOrigin = 'anonymous';
          img.onload = () => { this._imageCache[url] = img; resolve(img); };
          img.onerror = reject;
          img.src = url;
        });
      },

      initCanvas() {
        const container = this.$refs.canvasContainer;
        if (!container || typeof Konva === 'undefined') return;

        this._stage = new Konva.Stage({
          container,
          width: this.canvasWidth,
          height: this.canvasHeight,
        });
        this._layer = new Konva.Layer({ listening: false });
        this._stage.add(this._layer);

        this.fitStageToContainer();
        this.redrawCanvas();
      },

      fitStageToContainer() {
        if (!this._stage) return;
        const cw = this._stage.container().offsetWidth;
        if (!cw) return;
        const scale = cw / this.canvasWidth;
        this._stage.width(this.canvasWidth * scale);
        this._stage.height(this.canvasHeight * scale);
        this._stage.scale({ x: scale, y: scale });
        this._stage.batchDraw();
      },

      _baseImageUrl() {
        const mc = this._manifestCarrier();
        if (mc) {
          const set = (this.currentView === 'back' && mc.baseBack) ? mc.baseBack : mc.base;
          const file = findByColor(set, this.selectedColor) || Object.values(set)[0];
          if (file) return this._assetUrl(file);
        }
        if (!this.currentCarrier) return null;
        const imageSet = this.currentView === 'back'
          ? (this.currentCarrier.backImages || {})
          : (this.currentCarrier.baseImages || {});
        return findByColor(imageSet, this.selectedColor) || Object.values(imageSet)[0] || null;
      },

      // Ein einziger serialisierter Redraw-Pfad: Basis + Teile + Zonenrahmen.
      // Der Sequenz-Token verwirft überholte Läufe nach jedem await.
      async redrawCanvas() {
        if (!this._layer) return;
        const seq = ++this._drawSeq;
        const W = this.canvasWidth;
        const H = this.canvasHeight;
        const items = [];

        items.push({
          z: -1000,
          make: () => new Konva.Rect({ x: 0, y: 0, width: W, height: H, fill: '#111111' }),
        });

        const baseUrl = this._baseImageUrl();
        if (baseUrl) {
          try {
            const img = await this.loadImage(baseUrl);
            if (seq !== this._drawSeq) return;
            const s = Math.min(W / img.naturalWidth, H / img.naturalHeight);
            const bw = img.naturalWidth * s;
            const bh = img.naturalHeight * s;
            items.push({
              z: 0,
              make: () => new Konva.Image({ x: (W - bw) / 2, y: 0, width: bw, height: bh, image: img }),
            });
          } catch { /* Basisbild nicht ladbar */ }
        }

        const visibleZones = this.currentZones;
        const visibleZoneIds = new Set(visibleZones.map(z => z.id));
        for (const zone of visibleZones) {
          // Gestrichelte Outline nur für die aktive, unbesetzte Zone —
          // alle leeren Zonen gleichzeitig wirkt wie Fehlplatzierung.
          if (zone.id !== this.activeZone) continue;
          const hasAtt = this.selectedItems.some(a => a.zone === zone.id);
          if (hasAtt) continue;
          items.push({
            z: 1000,
            make: () => new Konva.Rect({
              x: (zone.x / 100) * W,
              y: (zone.y / 100) * H,
              width: (zone.width / 100) * W,
              height: (zone.height / 100) * H,
              stroke: 'rgba(255,255,255,0.08)',
              strokeWidth: 1,
              dash: [4, 4],
              cornerRadius: 4,
            }),
          });
        }

        for (const att of this.selectedItems) {
          if (!visibleZoneIds.has(att.zone)) continue;
          const url = this.getOverlayUrl(att);
          if (!url) continue;
          let img;
          try {
            img = await this.loadImage(url);
          } catch { continue; }
          if (seq !== this._drawSeq) return;

          const entry = this._manifestEntry(att.id);
          if (entry) {
            const x = entry.place.x * W;
            const y = entry.place.y * H;
            const w = entry.place.w * W;
            const h = w * (img.naturalHeight / img.naturalWidth);
            items.push({ z: entry.z, make: () => new Konva.Image({ x, y, width: w, height: h, image: img }) });
          } else {
            // Fallback ohne Manifest: contain-Fit im Zonen-Rechteck
            const zone = visibleZones.find(z => z.id === att.zone);
            if (!zone) continue;
            const zX = (zone.x / 100) * W;
            const zY = (zone.y / 100) * H;
            const zW = (zone.width / 100) * W;
            const zH = (zone.height / 100) * H;
            const pad = 0.05;
            const s = Math.min(zW * (1 - pad * 2) / img.naturalWidth, zH * (1 - pad * 2) / img.naturalHeight);
            const w = img.naturalWidth * s;
            const h = img.naturalHeight * s;
            items.push({
              z: 10,
              make: () => new Konva.Image({ x: zX + (zW - w) / 2, y: zY + (zH - h) / 2, width: w, height: h, image: img }),
            });
          }
        }

        if (seq !== this._drawSeq) return;
        this._layer.destroyChildren();
        items.sort((a, b) => a.z - b.z);
        for (const item of items) this._layer.add(item.make());
        this._layer.batchDraw();
      },

      // --- Cart ---

      async addToCart() {
        if (this.isAdding) return;
        this.isAdding = true;
        this.cartError = '';
        this.addedSuccess = false;

        try {
          const carrier = this.currentCarrier;
          if (!carrier) throw new Error('Kein Plattenträger ausgewählt');

          const carrierVariant = carrier.variants.find(v =>
            colorMatch(v.option1, this.selectedColor) && v.option2 === this.selectedSize && v.available
          );
          if (!carrierVariant) {
            throw new Error(`${carrier.title} ist in ${this.selectedColor} / ${this.selectedSize} nicht verfügbar`);
          }

          const bundleId = 'pc-' + Date.now().toString(36);
          const baseProps = { '_bundle_id': bundleId, '_configurator': 'plate-carrier' };
          const items = [{
            id: carrierVariant.id,
            quantity: 1,
            properties: { ...baseProps, '_zone': 'base', '_color': this.selectedColor, '_size': this.selectedSize },
          }];

          const skipped = [];
          for (const att of this.selectedItems) {
            const attVariant = this._attVariant(att);
            if (!attVariant) {
              skipped.push(att.title);
              continue;
            }
            items.push({
              id: attVariant.id,
              quantity: 1,
              properties: { ...baseProps, '_zone': att.zone, '_color': this.getAttColor(att.id) },
            });
          }
          if (skipped.length) {
            throw new Error(`Nicht verfügbar: ${skipped.join(', ')} — bitte Farbe ändern oder Teil entfernen`);
          }

          const response = await fetch(this.cartAddUrl, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json',
              'X-Requested-With': 'XMLHttpRequest',
            },
            body: JSON.stringify({
              items,
              sections: 'cart-drawer,cart-icon-bubble',
              sections_url: window.location.pathname,
            }),
          });
          if (!response.ok) {
            const errData = await response.json().catch(() => ({}));
            throw new Error(errData.description || 'Fehler beim Hinzufügen zum Warenkorb');
          }
          const result = await response.json();

          const cartRes = await fetch(
            `${window.Shopify?.routes?.root || '/'}cart.js`,
            { headers: { 'Accept': 'application/json' } }
          );
          const cart = await cartRes.json();
          document.dispatchEvent(new CustomEvent('cart:update', {
            bubbles: true,
            detail: { resource: cart, sourceId: 'configurator', data: { source: 'configurator', sections: result.sections || {} } },
          }));

          const cartDrawerEl = document.querySelector('cart-drawer-component');
          if (cartDrawerEl && typeof cartDrawerEl.open === 'function') {
            cartDrawerEl.open();
          } else {
            const drawer = document.querySelector('cart-drawer');
            if (drawer && typeof drawer.open === 'function') drawer.open();
          }

          this.addedSuccess = true;
          setTimeout(() => { this.addedSuccess = false; }, 3000);
        } catch (err) {
          this.cartError = err.message || 'Ein Fehler ist aufgetreten';
          setTimeout(() => { this.cartError = ''; }, 6000);
        } finally {
          this.isAdding = false;
        }
      },
    };
  });
}

document.addEventListener('alpine:init', registerConfigurator);
if (typeof Alpine !== 'undefined') registerConfigurator();
